import { Add } from '@mui/icons-material';
import { Box, Button, Stack, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { camelCaseToTitleCase } from '../../../../../shared/utils';
import { ServiceType } from '../../../../enums/contractor';
import { Contractor, ModuleContractor } from '../../../../types/contractor';
import {
    PartnerServiceType,
    PARTNER_SERVICE_TYPES,
    useOwnerContractors,
} from '../../Owner/contractorSelectionUtils';
import AddPartnerContractorDialog from './AddPartnerContractorDialog';
import ContractorSelectionCard from './ContractorSelectionCard';

type ModuleContractorSelection = { id: number };

const PartnerServiceSection = ({
    serviceType,
    selectedContractors,
    onRemoveContractor,
}: {
    serviceType: ServiceType;
    selectedContractors: ModuleContractorSelection[];
    onRemoveContractor: (id: number) => void;
}) => {
    const allContractors = useOwnerContractors();

    const contractorMap = new Map(allContractors.map((c) => [c.id, c]));

    const selectedIds = selectedContractors.map((item) => item.id);

    return (
        <Stack spacing={1.5}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                <Typography fontWeight='bold'>
                    {camelCaseToTitleCase(serviceType)}
                </Typography>
            </Box>

            {selectedIds.length === 0 ? (
                <Typography variant='body2' color='text.secondary'>
                    No contractors selected yet.
                </Typography>
            ) : (
                <Stack spacing={1.5}>
                    {selectedIds.map((id) => {
                        const contractor = contractorMap.get(id);
                        if (!contractor) {
                            return null;
                        }

                        return (
                            <Stack key={id} spacing={0.75}>
                                <ContractorSelectionCard
                                    contractor={contractor}
                                    mode='selected'
                                    onRemove={() => onRemoveContractor(id)}
                                />
                            </Stack>
                        );
                    })}
                </Stack>
            )}
        </Stack>
    );
};

const PartnerServicesSection = () => {
    const { control, setValue } = useFormContext();
    const [dialogOpen, setDialogOpen] = useState(false);

    const selectedContractors: Contractor[] =
        useWatch({ control, name: 'contractors' }) ?? [];
    const allContractors = useOwnerContractors();
    const contractorMap = new Map(allContractors.map((c) => [c.id, c]));
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [serviceType, setServiceType] = useState<PartnerServiceType>(
        PARTNER_SERVICE_TYPES[0],
    );

    const getContractorsByType = (
        serviceType: ServiceType,
    ): ModuleContractorSelection[] =>
        selectedContractors.filter((item) => {
            const contractor = contractorMap.get(item.id);
            return contractor?.services?.type === serviceType;
        });

    const selectedContractorsIds = selectedContractors.map((c) => c.id);
    const visibleTypes = new Set(
        allContractors
            .filter((contractor) =>
                selectedContractorsIds.includes(contractor.id),
            )
            .map((c) => c.services?.type),
    );

    const openDialog = () => {
        setSelectedIds(selectedContractors.map((c: ModuleContractor) => c.id));
        setDialogOpen(true);
    };

    const closeDialog = () => {
        setDialogOpen(false);
        setServiceType(PARTNER_SERVICE_TYPES[0]);
    };

    const handleRemoveContractor = (id: number) => {
        setValue(
            'contractors',
            selectedContractors.filter(
                (item: ModuleContractorSelection) => item.id !== id,
            ),
            { shouldDirty: true },
        );
    };

    const handleDialogConfirm = (selectedIds: number[]) => {
        const newContractors = selectedIds.map((id) => ({ id }));

        setValue('contractors', [...newContractors], {
            shouldDirty: true,
        });
    };

    const getSelectedIds = (serviceType: PartnerServiceType) =>
        getContractorsByType(serviceType).map((item) => item.id);

    // const selectedIds = ;

    return (
        <Box sx={{ pt: 2 }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    mb: 1,
                }}
            >
                <div>
                    <Typography
                        component='h4'
                        sx={{ color: 'text.primary', fontWeight: 'bold' }}
                    >
                        Partner Services
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                        Connect third-party contractors who offer catering,
                        wellness, workshops, alcohol services, and other custom
                        services for bookings.
                    </Typography>
                </div>
                <Button
                    variant='outlined'
                    startIcon={<Add />}
                    onClick={() => openDialog()}
                    sx={{
                        minWidth: 80,
                    }}
                >
                    New
                </Button>
            </Box>

            <AddPartnerContractorDialog
                open={dialogOpen}
                selectedIds={selectedIds}
                setSelectedIds={setSelectedIds}
                onClose={closeDialog}
                onConfirm={handleDialogConfirm}
                serviceType={serviceType}
                setServiceType={setServiceType}
            />

            <Stack spacing={2} sx={{ mt: 2 }}>
                {Array.from(visibleTypes).map((type) => (
                    <PartnerServiceSection
                        key={type}
                        serviceType={type}
                        selectedContractors={getContractorsByType(type)}
                        onRemoveContractor={handleRemoveContractor}
                    />
                ))}
            </Stack>
        </Box>
    );
};

export default PartnerServicesSection;
